// src/pages/CategoryGallery.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ref, onValue } from "firebase/database";
import { db } from "../lib/firebase";
import "./Gallery.css";
import { CONFIG } from "../config";
import useIsMobile from "../hooks/useIsMobile";
import { adjustFontSize } from "../utils/typography";
import { getNavSectionStyles } from "../utils/navSectionStyles";

const IMAGES_BASE = CONFIG.IMAGES_BASE;

const slug = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const asUrl = (val) => {
  if (!val) return "";
  if (val.startsWith("http")) return val;
  return `${IMAGES_BASE}/${encodeURIComponent(val)}`;
};

const asArray = (v) =>
  Array.isArray(v) ? v : v && typeof v === "object" ? Object.values(v) : [];

const resolveStyleValue = (customValue, fallbackValue) =>
  customValue !== undefined &&
  customValue !== null &&
  customValue !== ""
    ? customValue
    : fallbackValue;

export default function CategoryGallery() {
  const { category, subcategory } = useParams();
  const [cards, setCards] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [itemLabel, setItemLabel] = useState("");
  const [pageBg, setPageBg] = useState("#ffffff");
  const [homeCfg, setHomeCfg] = useState(null);
  const [homeReady, setHomeReady] = useState(false);
  const [galleryReady, setGalleryReady] = useState(false);
  const [intro, setIntro] = useState("");
  const [itemStyles, setItemStyles] = useState({});
  const [groupCardStyleConfig, setGroupCardStyleConfig] = useState({
    applyCardStyleToAllCards: false,
    cardTitleColor: "",
    cardTitleFontSize: "",
    cardDescriptionColor: "",
    cardDescriptionFontSize: "",
  });
  const [navSectionSource, setNavSectionSource] = useState({
    group: null,
    item: null,
  });
  const isMobile = useIsMobile();
  const [gridCols, setGridCols] = useState({ mobile: 2, desktop: 4 });

  // Lightbox states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const off = onValue(
      ref(db, "homepageconfig"),
      (snap) => {
        const v = snap.val() || {};
        setHomeCfg(v);
        setPageBg(v.backgroundColor || "#ffffff");
        setHomeReady(true);
      },
      (err) => {
        console.error("homepageconfig load error:", err);
        setHomeReady(true);
      }
    );
    return () => off();
  }, []);

  // Load gallery
  useEffect(() => {
    const off = onValue(
      ref(db, "navconfig/dropdowns"),
      (snap) => {
        const groups = asArray(snap.val());

        const g = groups.find((x) => slug(x.name) === slug(category));
        const items = asArray(g?.items);
        const it = items.find((x) => slug(x.label) === slug(subcategory));

        const images = asArray(it?.images).map((im) => ({
          url: asUrl(im?.filename || im?.image),
          metaTitle: (im?.metaTitle || "").trim(),
          metaDesc: (im?.metaDescription || "").trim(),
        }));

        setCards(images.filter((x) => x.url));
        setGroupName(g?.name || category);
        setItemLabel(it?.label || subcategory);
        setIntro(it?.metaDescription || g?.metaDescription || "");
        setGroupCardStyleConfig({
          applyCardStyleToAllCards: !!g?.applyCardStyleToAllCards,
          cardTitleColor: g?.cardTitleColor || "",
          cardTitleFontSize: g?.cardTitleFontSize ?? "",
          cardDescriptionColor: g?.cardDescriptionColor || "",
          cardDescriptionFontSize: g?.cardDescriptionFontSize ?? "",
        });
        setNavSectionSource({
          group: {
            specialCategory: !!g?.specialCategory,
            categoryGrid: !!g?.categoryGrid,
            latestCategory: !!g?.latestCategory,
            updatesCategory: !!g?.updatesCategory,
          },
          item: {
            specialCategory: !!it?.specialCategory,
            categoryGrid: !!it?.categoryGrid,
            latestCategory: !!it?.latestCategory,
            updatesCategory: !!it?.updatesCategory,
          },
        });
        setItemStyles({
          useSectionStyleConfig: !!it?.useSectionStyleConfig,
          titleColor: it?.titleColor || "",
          descriptionColor: it?.descriptionColor || "",
          titleFontSize: it?.titleFontSize ?? "",
          descriptionFontSize: it?.descriptionFontSize ?? "",
          metaTitleColor: it?.metaTitleColor || "",
          metaDescriptionColor: it?.metaDescriptionColor || "",
          metaTitleFontSize: it?.metaTitleFontSize ?? "",
          metaDescriptionFontSize: it?.metaDescriptionFontSize ?? "",
        });

        const refItem = items[0] || it;
        const mobileCols =
          refItem?.mobileCols !== undefined && refItem?.mobileCols !== null
            ? Number(refItem.mobileCols)
            : 2;
        const desktopCols =
          refItem?.desktopCols !== undefined && refItem?.desktopCols !== null
            ? Number(refItem.desktopCols)
            : 4;
        setGridCols({
          mobile: mobileCols > 0 ? mobileCols : 2,
          desktop: desktopCols > 0 ? desktopCols : 4,
        });

        setGalleryReady(true);
      },
      (err) => {
        console.error("navconfig/dropdowns load error:", err);
        setGalleryReady(true);
      }
    );

    return () => off();
  }, [category, subcategory]);

  const loading = !homeReady || !galleryReady;

  const heading = `${itemLabel} Designs`;
  const { fontStyles, sectionStyles } = getNavSectionStyles(
    homeCfg,
    navSectionSource?.group,
    navSectionSource?.item
  );
  const sizeOverrides = sectionStyles?.fontSizes || {};
  const sectionTitleFont = fontStyles?.sectionTitle || fontStyles?.heading || {};
  const sectionDescFont =
    fontStyles?.sectionDescription || fontStyles?.subHeading || {};
  const contentFont = fontStyles?.content || {};
  const useItemStyleConfig = !!itemStyles?.useSectionStyleConfig;
  const applyGroupCardStyleToAllCards =
    !!groupCardStyleConfig?.applyCardStyleToAllCards;

  const inkColor =
    sectionStyles?.headingColor ||
    sectionTitleFont?.color ||
    contentFont?.color ||
    "#1f1b17";
  const mutedColor =
    sectionStyles?.subHeadingColor || sectionDescFont?.color || "#6b5f54";
  const accentColor =
    sectionStyles?.contentColor ||
    fontStyles?.subHeading?.color ||
    sectionDescFont?.color ||
    "#b57e43";

  const titleStyle = {
    fontFamily: sectionTitleFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      resolveStyleValue(
        useItemStyleConfig ? itemStyles?.titleFontSize : undefined,
        sizeOverrides.heading || sectionTitleFont?.fontSize
      ) || undefined,
      isMobile
    ),
    fontWeight: sectionTitleFont?.fontWeight || 700,
    lineHeight: sectionTitleFont?.lineHeight || "1.2",
    color:
      resolveStyleValue(
        useItemStyleConfig ? itemStyles?.titleColor : undefined,
        sectionStyles?.headingColor || sectionTitleFont?.color
      ) || undefined,
  };

  const introStyle = {
    fontFamily: sectionDescFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      resolveStyleValue(
        useItemStyleConfig ? itemStyles?.descriptionFontSize : undefined,
        sizeOverrides.subHeading || sectionDescFont?.fontSize
      ) || undefined,
      isMobile
    ),
    fontWeight: sectionDescFont?.fontWeight || 400,
    lineHeight: sectionDescFont?.lineHeight || "1.6",
    color:
      resolveStyleValue(
        useItemStyleConfig ? itemStyles?.descriptionColor : undefined,
        sectionStyles?.subHeadingColor || sectionDescFont?.color
      ) || undefined,
  };

  const crumbStyle = {
    fontFamily: contentFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.content || contentFont?.fontSize || undefined,
      isMobile,
      3
    ),
    fontWeight: contentFont?.fontWeight || 400,
  };

  const metaTitleStyle = {
    fontFamily: sectionDescFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      resolveStyleValue(
        applyGroupCardStyleToAllCards
          ? groupCardStyleConfig?.cardTitleFontSize
          : useItemStyleConfig
          ? itemStyles?.metaTitleFontSize
          : undefined,
        sizeOverrides.content || sectionDescFont?.fontSize
      ) || undefined,
      isMobile,
      2
    ),
    fontWeight: sectionDescFont?.fontWeight || 600,
    color:
      resolveStyleValue(
        applyGroupCardStyleToAllCards
          ? groupCardStyleConfig?.cardTitleColor
          : useItemStyleConfig
          ? itemStyles?.metaTitleColor
          : undefined,
        sectionStyles?.contentColor || sectionDescFont?.color
      ) || undefined,
  };

  const metaDescStyle = {
    fontFamily: contentFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      resolveStyleValue(
        applyGroupCardStyleToAllCards
          ? groupCardStyleConfig?.cardDescriptionFontSize
          : useItemStyleConfig
          ? itemStyles?.metaDescriptionFontSize
          : undefined,
        sizeOverrides.content || contentFont?.fontSize
      ) || undefined,
      isMobile,
      3
    ),
    fontWeight: contentFont?.fontWeight || 400,
    color:
      resolveStyleValue(
        applyGroupCardStyleToAllCards
          ? groupCardStyleConfig?.cardDescriptionColor
          : useItemStyleConfig
          ? itemStyles?.metaDescriptionColor
          : undefined,
        sectionStyles?.contentColor || contentFont?.color
      ) || undefined,
  };

  const openLightbox = (i) => {
    setActiveIndex(i);
    setLightboxOpen(true);
  };
  const closeLightbox = () => setLightboxOpen(false);

  const showNext = (step) => {
    setActiveIndex((p) => (p + step + cards.length) % cards.length);
  };

  return (
    <main
      className="gallery-wrap"
      style={{
        background: pageBg,
        fontFamily: contentFont?.fontFamily || undefined,
        color: sectionStyles?.contentColor || contentFont?.color || undefined,
        "--gallery-ink": inkColor,
        "--gallery-muted": mutedColor,
        "--gallery-accent": accentColor,
      }}
    >
      <div className="gallery-inner">
        <div className="gallery-hero">
          {/* Breadcrumb */}
          <div className="breadcrumb" style={crumbStyle}>
            <Link to="/">Home</Link>
            <span> / </span>
            <Link to={`/gallery/${slug(groupName)}`}>{groupName}</Link>
            <span> / </span>
            <span className="crumb-current">{itemLabel}</span>
          </div>

          <h1 className="gallery-title" style={titleStyle}>
            <span className="accent-bar" /> {heading}
          </h1>

          {intro && (
            <p className="intro" style={introStyle}>
              {intro}
            </p>
          )}
        </div>

        {/* Grid */}
        <section
          className="cards-grid"
          style={{
            "--cg-mobile-cols": String(gridCols.mobile),
            "--cg-desktop-cols": String(gridCols.desktop),
          }}
        >
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="card skeleton" />
            ))}

          {!loading &&
            cards.map((c, i) => (
              <article key={i} className="card">
                <button
                  className="img-wrap"
                  onClick={() => openLightbox(i)}
                  type="button"
                >
                  <img src={c.url} alt={itemLabel} />
                </button>

                {(c.metaTitle || c.metaDesc) && (
                  <div className="meta">
                    {c.metaTitle && (
                      <h3 className="title" style={metaTitleStyle}>
                        {c.metaTitle}
                      </h3>
                    )}
                    {c.metaDesc && (
                      <p className="desc" style={metaDescStyle}>
                        {c.metaDesc}
                      </p>
                    )}
                  </div>
                )}
              </article>
            ))}
        </section>
      </div>

      {/* ===== LIGHTBOX ===== */}
      {lightboxOpen && (
        <div className="lightbox-backdrop" onClick={closeLightbox}>
          <div className="lightbox-dialog" onClick={(e) => e.stopPropagation()}>
            {/* Close */}
            <button className="lightbox-close" onClick={closeLightbox}>
              ×
            </button>

            {/* Arrows */}
            {cards.length > 1 && (
              <>
                <button
                  className="lightbox-arrow left"
                  onClick={() => showNext(-1)}
                >
                  ‹
                </button>
                <button
                  className="lightbox-arrow right"
                  onClick={() => showNext(1)}
                >
                  ›
                </button>
              </>
            )}

            {/* Image */}
            <img
              src={cards[activeIndex].url}
              alt=""
              className="lightbox-img"
            />

            {/* Caption */}
            {(cards[activeIndex].metaTitle ||
              cards[activeIndex].metaDesc) && (
              <div className="lightbox-caption">
                {cards[activeIndex].metaTitle && (
                  <div className="lightbox-title">
                    {cards[activeIndex].metaTitle}
                  </div>
                )}
                {cards[activeIndex].metaDesc && (
                  <div className="lightbox-desc">
                    {cards[activeIndex].metaDesc}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
